import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

interface ApprovalCounts {
  totalPending: number
  pendingGalleries: number
  pendingImages: number
  pendingReviews: number
}

const fetchApprovalCounts = async (): Promise<ApprovalCounts> => {
  try {
    // Get pending galleries count
    const { count: galleriesCount } = await supabase
      .from('galleries')
      .select('*', { count: 'exact', head: true })
      .eq('review_status', 'pending')

    // Get pending images count
    const { count: imagesCount } = await supabase
      .from('franchise_image_jobs')
      .select('*', { count: 'exact', head: true })
      .eq('review_status', 'pending')

    // Get pending reviews count (if GMB reviews table exists)
    let reviewsCount = 0
    try {
      const { count } = await supabase
        .from('gmb_reviews')
        .select('*', { count: 'exact', head: true })
        .eq('needs_response', true)
      reviewsCount = count || 0
    } catch (error) {
      // GMB reviews table might not exist, ignore error
      console.debug('GMB reviews table not found, skipping count')
    }

    const totalPending = (galleriesCount || 0) + (imagesCount || 0) + reviewsCount

    return {
      totalPending,
      pendingGalleries: galleriesCount || 0,
      pendingImages: imagesCount || 0,
      pendingReviews: reviewsCount
    }
  } catch (error) {
    console.error('Error fetching approval counts:', error)
    return {
      totalPending: 0,
      pendingGalleries: 0,
      pendingImages: 0,
      pendingReviews: 0
    }
  }
}

export function useApprovalCounts() {
  return useQuery({
    queryKey: ['approval-counts'],
    queryFn: fetchApprovalCounts,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
    refetchIntervalInBackground: false,
    retry: 1
  })
}

export default useApprovalCounts